/**
 * The editor's Agent and Model fields. With the editor options, both are
 * selects of what this project can run: the agents dispatch would see with
 * Agent runs on, and the selected agent's model set (the dispatch ceiling,
 * from the gateway's models). "Default" names what an unset field resolves
 * to. A value the YAML holds that is not on offer stays selectable, marked,
 * so the form never drops it silently; lint reports it. Without the options
 * (still loading, or failed), the model is typed.
 */

import type { ReactElement } from 'react';
import type { AgentChoice } from '../utils/definitionForm';
import type { EditorOptions } from '../schemas/editorOptions.schemas';

interface AgentModelFieldsProps {
  id: string;
  agent: AgentChoice;
  model: string;
  options: EditorOptions | undefined;
  onAgent: (agent: AgentChoice) => void;
  onModel: (model: string) => void;
}

const AGENT_LABELS: Record<'pi' | 'claude-code', string> = { pi: 'Pi', 'claude-code': 'Claude Code' };

function agentChoices(agent: AgentChoice, options: EditorOptions | undefined): [AgentChoice, string][] {
  if (options === undefined) {
    return [['', '(default: Pi)'], ['pi', AGENT_LABELS.pi], ['claude-code', AGENT_LABELS['claude-code']]];
  }
  const fallback = options.default_agent === null ? '(default: none available)' : `(default: ${AGENT_LABELS[options.default_agent]})`;
  const out: [AgentChoice, string][] = [['', fallback]];
  for (const a of options.agents) {
    if (a.available) out.push([a.name, AGENT_LABELS[a.name]]);
    else if (a.name === agent) out.push([a.name, `${AGENT_LABELS[a.name]} (not assigned to this project)`]);
  }
  return out;
}

function ModelField(props: { id: string; model: string; models: string[] | null; onModel: (m: string) => void }): ReactElement {
  const { id, model, models, onModel } = props;
  if (models === null) {
    return (
      <input id={id} type="text" placeholder="the agent's default" autoComplete="off" value={model}
        onChange={(e) => { onModel(e.target.value); }} />
    );
  }
  const first = models[0];
  return (
    <select id={id} value={model} onChange={(e) => { onModel(e.target.value); }}>
      <option value="">{first === undefined ? 'Default' : `Default (${first})`}</option>
      {models.map((m) => <option key={m} value={m}>{m}</option>)}
      {model !== '' && !models.includes(model) && <option value={model}>{model} (not allowed)</option>}
    </select>
  );
}

export function AgentModelFields({ id, agent, model, options, onAgent, onModel }: AgentModelFieldsProps): ReactElement {
  const effective = agent === '' ? options?.default_agent ?? null : agent;
  const models = options === undefined ? null : options.agents.find((a) => a.name === effective)?.models ?? [];
  return (
    <>
      <div className="form-group def-form__field">
        <label htmlFor={`${id}-agent`}>Agent</label>
        <select id={`${id}-agent`} value={agent} onChange={(e) => { onAgent(e.target.value as AgentChoice); }}>
          {agentChoices(agent, options).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="form-group def-form__field">
        <label htmlFor={`${id}-model`}>Model</label>
        <ModelField id={`${id}-model`} model={model} models={models} onModel={onModel} />
      </div>
    </>
  );
}
